import { DashboardPeriodSelection } from '@app/domain/models/dashboard-period.model';

/** Fechas que `GET /api/v1/tpv/dashboard/` recibe como `start_date` / `end_date`. */
export interface TpvDashboardDateRange {
  startDate: string;
  endDate: string;
}

/** Hora con la que viaja el día final: el back filtra hasta el último segundo del día. */
const FIN_DEL_DIA = 'T23:59:59';

/**
 * Traduce el periodo activo del dashboard de TPV a las dos fechas del resumen
 * (feature 153, R7 y R10). Puro y sin reloj propio: `ahora` entra por parámetro para
 * poder fijarlo en las pruebas.
 *
 * - Periodos fijos: EXACTAMENTE lo que calculaba el `buildQuery()` del componente antes
 *   de esta feature — hoy, hoy − 6 días, día 1 del mes, 1 de enero — hasta hoy a las
 *   `23:59:59`. Cambiarlo cambiaría lo que el operador ya ve con "Hoy", "Semana", "Mes"
 *   y "Año".
 * - Rango específico: sus dos días calendario, el final a las `23:59:59`.
 *
 * Todas las fechas salen de los componentes LOCALES (las del rango ya vienen así de
 * `buildCustomPeriod` → `formatLocalIsoDate`), nunca de `toISOString()`: al este de UTC
 * eso corre la ventana un día.
 *
 * La validación del rango (completo, no invertido, dentro de los 12 meses) NO vive aquí:
 * la hace el contenedor antes de fijar el periodo, con `buildCustomPeriod` +
 * `isSelectableDashboardRange`. Esta función solo traduce un periodo ya aceptado.
 */
export function tpvDashboardDateRange(
  seleccion: DashboardPeriodSelection,
  ahora: Date,
): TpvDashboardDateRange {
  if (seleccion.kind === 'custom') {
    return { startDate: seleccion.from, endDate: `${seleccion.to}${FIN_DEL_DIA}` };
  }

  const inicio = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  switch (seleccion.kind) {
    case 'semana':
      inicio.setDate(inicio.getDate() - 6);
      break;
    case 'mes':
      inicio.setDate(1);
      break;
    case 'año':
      inicio.setMonth(0, 1);
      break;
    // 'hoy' → el inicio ya es hoy.
  }

  return {
    startDate: diaLocal(inicio),
    endDate: `${diaLocal(ahora)}${FIN_DEL_DIA}`,
  };
}

/** `YYYY-MM-DD` por componentes LOCALES (el mismo formato que usaba el `formatDate` del componente). */
function diaLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dia}`;
}
